/**
 * Convert a price from one currency to another using exchange rates
 * @param amount The price amount to convert
 * @param fromCurrency The source currency code (e.g., "USD")
 * @param toCurrency The target currency code (e.g., "EUR")
 * @param rates Exchange rates object with currency codes as keys
 * @returns The converted price amount
 */
export function convertPrice(
    amount: number,
    fromCurrency: string,
    toCurrency: string,
    rates: Record<string, number>
): number {
    // If currencies are the same, no conversion needed
    if (fromCurrency === toCurrency) {
        return amount
    }

    // Check if we have the required rates
    if (!rates[fromCurrency] || !rates[toCurrency]) {
        console.warn(
            `Missing exchange rate for ${fromCurrency} or ${toCurrency}, returning original amount`
        )
        return amount
    }

    // All rates are relative to AUD (base currency)
    // To convert from one currency to another:
    // 1. Convert from source currency to AUD: amount / rates[fromCurrency]
    // 2. Convert from AUD to target currency: result * rates[toCurrency]

    const amountInBase = amount / rates[fromCurrency]
    const convertedAmount = amountInBase * rates[toCurrency]

    return convertedAmount
}

/**
 * Get the display price for a product based on user's preferred currency
 * @param originalPrice The original product price
 * @param originalCurrency The product's currency
 * @param preferredCurrency The user's preferred currency (optional)
 * @param rates The exchange rates (optional)
 * @returns Object with displayPrice and displayCurrency
 */
export function getDisplayPrice(
    originalPrice: number,
    originalCurrency: string,
    preferredCurrency?: string,
    rates?: Record<string, number>
): { displayPrice: number; displayCurrency: string } {
    // If no preferred currency or user selected "ORIGINAL", use original price/currency
    if (!preferredCurrency || preferredCurrency === 'ORIGINAL' || !rates) {
        return {
            displayPrice: originalPrice,
            displayCurrency: originalCurrency,
        }
    }

    // Convert price to preferred currency
    const displayPrice = convertPrice(
        originalPrice,
        originalCurrency,
        preferredCurrency,
        rates
    )

    return {
        displayPrice,
        displayCurrency: preferredCurrency,
    }
}
